<template>
  <div
    class="stockhist"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="titleId"
    @keydown.esc.prevent="emit('close')"
  >
    <button type="button" class="stockhist__catch" aria-label="닫기" @click="emit('close')" />
    <div class="stockhist__panel" tabindex="-1" ref="panelEl">
      <header class="stockhist__head">
        <div>
          <h2 :id="titleId">재고 · 판매 이력</h2>
          <p class="stockhist__meta">
            <strong>{{ detail?.name || productName || '상품을 선택하세요' }}</strong>
            <span v-if="detail?.code || productCode">{{ detail?.code || productCode }}</span>
            <span v-if="detail?.storeName">{{ detail.storeName }}</span>
          </p>
        </div>
        <button type="button" class="stockhist__close" aria-label="재고 닫기" @click="emit('close')">
          <AppIcon icon="mdi-close" :size="20" />
        </button>
      </header>

      <label v-if="candidates.length" class="stockhist__pick">
        <span>상품</span>
        <select class="sui-input" :value="productCode" @change="onPick">
          <option value="">상품을 선택하세요</option>
          <option v-for="item in candidates" :key="item.code" :value="item.code">
            {{ item.name }}
          </option>
        </select>
      </label>

      <div v-if="loading" class="stockhist__loading">
        <AppIcon icon="mdi-loading" :size="24" />
        <p>불러오는 중…</p>
      </div>

      <template v-else-if="detail">
        <section class="stockhist__summary" data-tour="stock-history-summary">
          <div>
            <span>현재 재고</span>
            <strong :class="{ 'is-low': detail.stock <= 0 }">{{ detail.stock.toLocaleString('ko-KR') }}개</strong>
          </div>
          <div>
            <span>누적 판매</span>
            <strong>{{ detail.soldCount.toLocaleString('ko-KR') }}개</strong>
          </div>
        </section>

        <section class="stockhist__add" data-tour="stock-history-add">
          <h3>재고 추가</h3>
          <p class="stockhist__hint">입고 수량을 넣으면 이력에 남고, 현재 재고에 더해집니다.</p>
          <div class="stockhist__add-row">
            <label class="stockhist__sr" for="stockhist-add">추가 수량</label>
            <input
              id="stockhist-add"
              v-model.number="addQty"
              class="sui-input"
              type="number"
              min="1"
              step="1"
              placeholder="예: 20"
              :disabled="saving"
              @keydown.enter.prevent="onAdd"
            />
            <button
              type="button"
              class="sui-btn sui-btn--primary"
              :disabled="saving || !canAdd"
              @click="onAdd"
            >
              {{ saving ? '반영 중…' : '재고 추가' }}
            </button>
          </div>
          <p v-if="formError" class="stockhist__error">{{ formError }}</p>
        </section>

        <section class="stockhist__timeline" data-tour="stock-history-timeline">
          <h3>이력</h3>
          <ol v-if="timeline.length" class="stockhist__list">
            <li v-for="(item, index) in timeline" :key="`${item.kind}-${item.at}-${index}`">
              <div class="stockhist__row-top">
                <span class="stockhist__badge" :class="badgeClass(item)">
                  {{ badgeLabel(item) }}
                </span>
                <time>{{ formatDateTime(item.at) }}</time>
              </div>
              <p v-if="item.kind === 'sale'" class="stockhist__row-main">
                <strong class="is-sale">−{{ item.qty.toLocaleString('ko-KR') }}개</strong>
                <span>{{ item.buyerOrg }}</span>
                <em>{{ item.orderCode }}</em>
              </p>
              <p v-else class="stockhist__row-main">
                <strong :class="item.memo.includes('복구') ? 'is-sale' : 'is-add'">
                  {{ item.memo || '재고 조정' }}
                </strong>
                <span>{{ actorLabel(item) }}</span>
              </p>
              <p v-if="item.kind === 'sale' && (item.option || item.name)" class="stockhist__row-sub">
                {{ item.name }}<template v-if="item.option"> · {{ item.option }}</template>
              </p>
            </li>
          </ol>
          <div v-else class="stockhist__empty-box">
            <p>아직 판매·입고 이력이 없습니다.</p>
            <p class="stockhist__empty-guide">
              입금 확인된 주문이 생기면 판매 차감이 여기에 쌓이고, 아래에서 입고하면 시점이 남습니다.
            </p>
            <div class="stockhist__quick">
              <button type="button" class="sui-btn sui-btn--sm" :disabled="saving" @click="quickAdd(10)">
                +10 입고
              </button>
              <button type="button" class="sui-btn sui-btn--sm" :disabled="saving" @click="quickAdd(20)">
                +20 입고
              </button>
              <button type="button" class="sui-btn sui-btn--sm" :disabled="saving" @click="quickAdd(50)">
                +50 입고
              </button>
            </div>
          </div>
        </section>
      </template>

      <p v-else-if="productCode" class="stockhist__empty">이력을 불러오지 못했습니다.</p>
      <p v-else class="stockhist__empty">상품을 고르면 재고와 입고·판매 이력이 열립니다.</p>

      <footer class="stockhist__foot">
        <button type="button" class="sui-btn sui-btn--ghost" @click="emit('close')">닫기</button>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  addAdminProductStock,
  fetchAdminProductStockHistory,
} from '@/api/admin'
import {
  addSellerProductStock,
  fetchSellerProductStockHistory,
  type ProductStockAdjustRow,
  type ProductStockHistory,
  type ProductStockSaleRow,
} from '@/api/mallProduct'

const props = withDefaults(
  defineProps<{
    productCode: string
    productName?: string
    audience: 'seller' | 'admin'
    candidates?: { code: string; name: string }[]
  }>(),
  { candidates: () => [] },
)

const emit = defineEmits<{
  close: []
  select: [code: string]
  updated: [payload: { code: string; stock: number; status?: string }]
}>()

const titleId = `stockhist-${Math.random().toString(36).slice(2, 7)}`
const panelEl = ref<HTMLElement | null>(null)
const loading = ref(Boolean(props.productCode))
const saving = ref(false)
const detail = ref<ProductStockHistory | null>(null)
const addQty = ref<number | null>(null)
const formError = ref('')

type TimelineSale = ProductStockSaleRow & { kind: 'sale' }
type TimelineAdjust = ProductStockAdjustRow & { kind: 'adjust' }
type TimelineItem = TimelineSale | TimelineAdjust

const canAdd = computed(() => {
  const n = Number(addQty.value)
  return Number.isFinite(n) && Math.trunc(n) >= 1
})

const timeline = computed<TimelineItem[]>(() => {
  if (!detail.value) return []
  const sales: TimelineItem[] = (detail.value.sales || []).map((row) => ({ ...row, kind: 'sale' as const }))
  const adjusts: TimelineItem[] = (detail.value.adjustments || []).map((row) => ({
    ...row,
    kind: 'adjust' as const,
  }))
  return [...sales, ...adjusts].sort((a, b) => {
    const ta = new Date(a.at).getTime()
    const tb = new Date(b.at).getTime()
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0
    if (Number.isNaN(ta)) return 1
    if (Number.isNaN(tb)) return -1
    return tb - ta
  })
})

function roleLabel(role: string): string {
  if (role === 'ADMIN') return '관리자'
  if (role === 'SELLER') return '판매업체'
  return role || ''
}

/** 내부 코드(SLR…)는 사람에게 안 보여 준다. */
function actorLabel(item: { role: string; actor: string }): string {
  if (item.role === 'ADMIN') return '관리자'
  if (item.role === 'SELLER') {
    const store = detail.value?.storeName?.trim()
    if (props.audience === 'admin' && store) return store
    return '판매업체'
  }
  return roleLabel(item.role)
}

function badgeLabel(item: TimelineItem): string {
  if (item.kind === 'sale') return '판매'
  if (item.memo.includes('복구')) return '복구'
  if (item.memo.includes('추가')) return '입고'
  return '조정'
}

function badgeClass(item: TimelineItem): string {
  if (item.kind === 'sale') return 'is-sale'
  if (item.memo.includes('복구')) return 'is-restore'
  return 'is-adjust'
}

function formatDateTime(value?: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16)
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
    date.getDate(),
  ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

function onPick(event: Event) {
  emit('select', (event.target as HTMLSelectElement).value)
}

async function load() {
  if (!props.productCode) {
    loading.value = false
    detail.value = null
    formError.value = ''
    return
  }
  loading.value = true
  formError.value = ''
  try {
    detail.value =
      props.audience === 'admin'
        ? await fetchAdminProductStockHistory(props.productCode)
        : await fetchSellerProductStockHistory(props.productCode)
  } catch (error) {
    detail.value = null
    formError.value = error instanceof Error ? error.message : '이력을 불러오지 못했습니다.'
  } finally {
    loading.value = false
    requestAnimationFrame(() => panelEl.value?.focus())
  }
}

async function onAdd() {
  if (!canAdd.value || saving.value || !detail.value) return
  const qty = Math.trunc(Number(addQty.value))
  formError.value = ''
  saving.value = true
  try {
    if (props.audience === 'admin') {
      const product = await addAdminProductStock(props.productCode, qty)
      emit('updated', { code: product.code, stock: product.stock, status: product.status })
    } else {
      const data = await addSellerProductStock(props.productCode, qty)
      emit('updated', {
        code: props.productCode,
        stock: data.stock,
        status: data.status,
      })
    }
    addQty.value = null
    await load()
  } catch (error) {
    formError.value = error instanceof Error ? error.message : '재고 추가에 실패했습니다.'
  } finally {
    saving.value = false
  }
}

async function quickAdd(qty: number) {
  addQty.value = qty
  await onAdd()
}

watch(
  () => props.productCode,
  () => {
    void load()
  },
)

onMounted(() => {
  document.body.style.overflow = 'hidden'
  void load()
})

onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<style lang="scss" src="./ProductStockHistoryModal.scss"></style>
